import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { usePageLoading } from '../contexts/PageLoadingContext';
import { useAuth } from '../contexts/AuthContext';

interface Dependent {
  id: string;
  fullName: string;
  email: string;
  confirmed?: boolean;
}

interface ProfileData {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  requirePasswordConfirmation: boolean;
  dependents: Dependent[];
}

export function EditProfilePage() {
  const { id: residenceId } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { startPageLoading, stopPageLoading } = usePageLoading();
  const { updateUser } = useAuth();

  // Password field refs (uncontrolled - allows browser autofill)
  const profilePasswordRef = useRef<HTMLInputElement>(null);
  const newPasswordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);
  const changePasswordRef = useRef<HTMLInputElement>(null);
  const profileFormRef = useRef<HTMLFormElement>(null);

  // Profile form state
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [requirePasswordConfirmation, setRequirePasswordConfirmation] = useState(true);

  // Dependent form state
  const [dependents, setDependents] = useState<Dependent[]>([]);
  const [newDependentEmail, setNewDependentEmail] = useState('');
  const [showAddDependent, setShowAddDependent] = useState(false);

  // Error state
  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({});
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});

  // Flash messages
  const [flashNotice, setFlashNotice] = useState('');
  const [flashAlert, setFlashAlert] = useState('');

  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmittingProfile, setIsSubmittingProfile] = useState(false);
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);
  const [isSubmittingDependent, setIsSubmittingDependent] = useState(false);

  // Esconde o preloader apenas APÓS o React renderizar com isLoading = false
  useEffect(() => {
    if (!isLoading) {
      stopPageLoading();
    }
  }, [isLoading, stopPageLoading]);

  // Fetch profile data on mount
  useEffect(() => {
    const loadProfileData = async () => {
      startPageLoading();
      try {
        const response = await api.get<ProfileData>(`/condomino/${residenceId}/api_data`);
        const data = response.data;
        setEmail(data.email || '');
        setFirstName(data.firstName || '');
        setLastName(data.lastName || '');
        setPhoneNumber(data.phoneNumber || '');
        setRequirePasswordConfirmation(data.requirePasswordConfirmation ?? true);
        setDependents(data.dependents || []);
      } catch (err) {
        console.error('Error loading profile data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    if (residenceId) {
      loadProfileData();
    }
  }, [residenceId, startPageLoading]);

  // Handle profile form submission
  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileErrors({});
    setFlashNotice('');
    setFlashAlert('');
    setIsSubmittingProfile(true);

    const currentPassword = profilePasswordRef.current?.value || '';

    try {
      await api.put(`/condomino/${residenceId}/salvar`, {
        user: {
          email,
          first_name: firstName,
          last_name: lastName,
          phone_number: phoneNumber,
          current_password: currentPassword,
        }
      });
      
      // Atualiza o nome no contexto de autenticação (reflete no header)
      updateUser({ 
        fullName: `${firstName} ${lastName}`.trim(),
        email 
      });
      
      setFlashNotice(t('views.devise.registrations.profile_updated', 'Perfil atualizado com sucesso'));
      if (profilePasswordRef.current) {
        profilePasswordRef.current.value = '';
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 
        t('views.devise.registrations.update_failed', 'Não foi possível realizar a atualização');
      setFlashAlert(errorMessage);
    } finally {
      setIsSubmittingProfile(false);
    }
  };

  // Handle password form submission
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordErrors({});
    setFlashNotice('');
    setFlashAlert('');

    const newPassword = newPasswordRef.current?.value || '';
    const confirmPassword = confirmPasswordRef.current?.value || '';
    const currentPasswordChange = changePasswordRef.current?.value || '';

    // Client-side validation
    const errors: Record<string, string> = {};
    if (!newPassword || newPassword.trim() === '') {
      errors.password = 'A nova senha é obrigatória';
    }
    if (!confirmPassword || confirmPassword.trim() === '') {
      errors.password_confirmation = 'A confirmação de senha é obrigatória';
    }
    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      errors.password_confirmation = 'As senhas não conferem';
    }
    if (requirePasswordConfirmation && (!currentPasswordChange || currentPasswordChange.trim() === '')) {
      errors.current_password = 'A senha atual é obrigatória';
    }

    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }

    setIsSubmittingPassword(true);

    try {
      await api.patch(`/condomino/${residenceId}/alterar-senha`, {
        user: {
          password: newPassword,
          password_confirmation: confirmPassword,
          current_password: currentPasswordChange,
        }
      });
      setFlashNotice(t('views.devise.registrations.password_updated', 'Senha alterada com sucesso'));
      // Clear password fields
      if (newPasswordRef.current) newPasswordRef.current.value = '';
      if (confirmPasswordRef.current) confirmPasswordRef.current.value = '';
      if (changePasswordRef.current) changePasswordRef.current.value = '';
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 
        t('views.devise.registrations.update_failed', 'Não foi possível realizar a atualização');
      setFlashAlert(errorMessage);
    } finally {
      setIsSubmittingPassword(false);
    }
  };

  // Handle add dependent
  const handleAddDependent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDependentEmail.trim()) return;

    setFlashNotice('');
    setFlashAlert('');
    setIsSubmittingDependent(true);

    try {
      await api.post(`/condomino/${residenceId}/create`, {
        user: {
          email: newDependentEmail,
        }
      });

      // Reload dependents
      const profileResponse = await api.get<ProfileData>(`/condomino/${residenceId}/api_data`);
      setDependents(profileResponse.data.dependents || []);
      setNewDependentEmail('');
      setShowAddDependent(false);
      setFlashNotice(t('views.devise.registrations.dependent_added', 'Membro adicionado. Ele receberá um e-mail para confirmar o acesso.'));
    } catch (err: any) {
      setFlashAlert(err.response?.data?.error || 'Erro ao adicionar dependente');
    } finally {
      setIsSubmittingDependent(false);
    }
  };

  // Handle delete dependent
  const handleDeleteDependent = async (dependentId: string) => {
    try {
      await api.delete(`/condomino/${residenceId}/destroy/${dependentId}`);
      setDependents(dependents.filter(d => d.id !== dependentId));
    } catch (err: any) {
      setFlashAlert(err.response?.data?.error || 'Erro ao excluir dependente');
    }
  };

  // Não renderiza nada enquanto carrega - preloader global está visível
  if (isLoading) {
    return null;
  }

  return (
    <>
      {/* Flash messages */}
      {flashNotice && (
        <div className="w-full px-[15px]">
          <div 
            className="
              relative
              pl-[60px] pr-12 py-[12px]
              !bg-[#2dcc6f] text-white
              rounded
              overflow-hidden
              after:content-[''] after:absolute after:w-[57px] after:h-[52px]
              after:bg-[url('/images/icons-set.png')] after:bg-no-repeat
              after:top-[14px] after:left-[5px]
              after:bg-[position:-58px_-166px]
            " 
            role="alert"
          >
            <button 
              type="button" 
              className="absolute top-[11px] right-[15px] p-0 bg-transparent border-0 text-[21px] font-bold leading-none cursor-pointer"
              onClick={() => setFlashNotice('')}
              aria-label="Fechar"
            >
              <span className="text-white text-[28px] font-black opacity-100 hover:opacity-80">×</span>
            </button>
            <strong className="block font-bold">{t('common.success', 'Sucesso!')}</strong>
            <small className="text-[80%] font-normal">{flashNotice}</small>
          </div>
        </div>
      )}
      {flashAlert && (
        <div className="w-full px-[15px]">
          <div 
            className="
              relative
              pl-[60px] pr-12 py-[12px]
              !bg-[#e84b3d] text-white
              rounded
              overflow-hidden
              after:content-[''] after:absolute after:w-[57px] after:h-[52px]
              after:bg-[url('/images/icons-set.png')] after:bg-no-repeat
              after:top-[14px] after:left-[-11px]
              after:bg-[position:11px_-226px]
            " 
            role="alert"
          >
            <button 
              type="button" 
              className="absolute top-[11px] right-[15px] p-0 bg-transparent border-0 text-[21px] font-bold leading-none cursor-pointer"
              onClick={() => setFlashAlert('')}
              aria-label="Fechar"
            >
              <span className="text-white text-[28px] font-black opacity-100 hover:opacity-80">×</span>
            </button>
            <strong className="block font-bold">{t('common.error', 'Erro!')}</strong>
            <small className="text-[80%] font-normal">{flashAlert}</small>
          </div>
        </div>
      )}

      {/* Title section */}
      <div className="w-full px-[15px]">
        <div className="mb-4">
          <h4 className="text-[#7b8aa4] text-[18px] font-['Poppins',sans-serif] font-medium">
            {t('views.devise.registrations.title', 'Perfil do condômino')}
          </h4>
          <p className="text-[#666] text-[14px] mt-2">
            {t('views.devise.registrations.subtitle', 'Aqui você pode alterar os dados do seu perfil e sua senha.')}
          </p>
        </div>
      </div>

      {/* Change Profile Form */}
      <div className="w-full md:w-1/2 px-[15px]">
        <div className="p-4">
          <div className="mt-5">
            <p className="text-[#6c757d] text-[14px] font-medium mb-4">
              <span>{t('views.devise.registrations.change_profile', 'Altere os dados do seu perfil')}</span>
            </p>
            <form ref={profileFormRef} onSubmit={handleProfileSubmit} role="form">
              <div className="flex flex-wrap mb-4">
                <label htmlFor="user_email" className="w-full md:w-1/4 text-[12px] py-[5px] md:pr-[10px]">
                  {t('views.devise.registrations.email', 'Email')}
                </label>
                <div className="w-full md:w-[41.666667%]">
                  <input
                    type="email"
                    id="user_email"
                    name="user[email]"
                    className="w-full px-3 py-[10px] text-[12px] text-[#495057] h-11 border-0 rounded-[20px] bg-white shadow-none appearance-none focus:outline-none font-['Poppins',sans-serif]"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoFocus
                  />
                  {profileErrors.email && (
                    <span className="text-[#e84b3d] text-[12px] float-right">{profileErrors.email}</span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap mb-4">
                <label htmlFor="user_first_name" className="w-full md:w-1/4 text-[12px] py-[5px] md:pr-[10px]">
                  {t('views.devise.registrations.name', 'Nome')}
                </label>
                <div className="w-full md:w-[41.666667%]">
                  <input
                    type="text"
                    id="user_first_name"
                    name="user[first_name]"
                    className="w-full px-3 py-[10px] text-[12px] text-[#495057] h-11 border-0 rounded-[20px] bg-white shadow-none appearance-none focus:outline-none font-['Poppins',sans-serif]"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  {profileErrors.first_name && (
                    <span className="text-[#e84b3d] text-[12px] float-right">{profileErrors.first_name}</span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap mb-4">
                <label htmlFor="user_last_name" className="w-full md:w-1/4 text-[12px] py-[5px] md:pr-[10px]">
                  {t('views.devise.registrations.last_name', 'Sobrenome')}
                </label>
                <div className="w-full md:w-[41.666667%]">
                  <input
                    type="text"
                    id="user_last_name"
                    name="user[last_name]"
                    className="w-full px-3 py-[10px] text-[12px] text-[#495057] h-11 border-0 rounded-[20px] bg-white shadow-none appearance-none focus:outline-none font-['Poppins',sans-serif]"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                  {profileErrors.last_name && (
                    <span className="text-[#e84b3d] text-[12px] float-right">{profileErrors.last_name}</span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap mb-4">
                <label htmlFor="user_phone_number" className="w-full md:w-1/4 text-[12px] py-[5px] md:pr-[10px]">
                  {t('views.devise.registrations.cellphone', 'Celular')}
                </label>
                <div className="w-full md:w-[41.666667%]">
                  <input
                    type="text"
                    id="user_phone_number"
                    name="user[phone_number]"
                    className="w-full px-3 py-[10px] text-[12px] text-[#495057] h-11 border-0 rounded-[20px] bg-white shadow-none appearance-none focus:outline-none font-['Poppins',sans-serif]"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                  />
                  {profileErrors.phone_number && (
                    <span className="text-[#e84b3d] text-[12px] float-right">{profileErrors.phone_number}</span>
                  )}
                </div>
              </div>
              {requirePasswordConfirmation && (
                <>
                  <div className="flex flex-wrap mb-2">
                    <label className="w-full md:w-[66.666667%] text-[12px] py-[5px]">
                      {t('views.devise.registrations.inform_password', 'Por questões de segurança, informe sua senha para confirmar alteração')}
                    </label>
                  </div>
                  <div className="flex flex-wrap mb-4">
                    <label htmlFor="profile_current_password" className="w-full md:w-1/4 text-[12px] py-[5px] md:pr-[10px]">
                      {t('views.devise.registrations.password', 'Senha atual')}
                    </label>
                    <div className="w-full md:w-[41.666667%]">
                      <input
                        type="password"
                        id="profile_current_password"
                        name="user[current_password]"
                        className="w-full px-3 py-[10px] text-[12px] text-[#495057] h-11 border-0 rounded-[20px] bg-white shadow-none appearance-none focus:outline-none font-['Poppins',sans-serif]"
                        ref={profilePasswordRef}
                      />
                      {profileErrors.current_password && (
                        <span className="text-[#e84b3d] text-[12px] float-right">{profileErrors.current_password}</span>
                      )}
                    </div>
                  </div>
                </>
              )}
              <div className="flex flex-wrap mb-4">
                <div className="hidden md:block md:w-1/4"></div>
                <div className="w-full md:w-[41.666667%] flex justify-end">
                  <button 
                    type="submit" 
                    className="w-full md:w-auto px-6 py-2 bg-[#2988b5] !text-white !opacity-100 text-[12px] font-semibold rounded-[20px] min-w-[157px] text-center border-0 hover:bg-[#4a9abf] disabled:!opacity-50 disabled:cursor-not-allowed"
                    disabled={isLoading || isSubmittingProfile}
                  >
                    {t('views.devise.registrations.submit_profile', 'Alterar perfil')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Change Password Form */}
      <div className="w-full md:w-1/2 px-[15px]">
        <div className="p-4">
          <div className="mt-5">
            <p className="text-[#6c757d] text-[14px] font-medium mb-4">
              <span>{t('views.devise.registrations.change_password', 'Altere sua senha')}</span>
            </p>
            <form onSubmit={handlePasswordSubmit} role="form">
              <div className="flex flex-wrap mb-4">
                <label htmlFor="newPassword" className="w-full md:w-1/4 text-[12px] py-[5px] md:pr-[10px]">
                  {t('views.devise.registrations.new_password', 'Nova senha')}
                </label>
                <div className="w-full md:w-[41.666667%]">
                  <input
                    type="password"
                    id="newPassword"
                    name="user[password]"
                    className="w-full px-3 py-[10px] text-[12px] text-[#495057] h-11 border-0 rounded-[20px] bg-white shadow-none appearance-none focus:outline-none font-['Poppins',sans-serif]"
                    ref={newPasswordRef}
                  />
                  {passwordErrors.password && (
                    <span className="text-[#e84b3d] text-[12px] float-right">{passwordErrors.password}</span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap mb-4">
                <label htmlFor="confirmPassword" className="w-full md:w-1/4 text-[12px] py-[5px] md:pr-[10px]">
                  {t('views.devise.registrations.confirm_password', 'Confirme a senha')}
                </label>
                <div className="w-full md:w-[41.666667%]">
                  <input
                    type="password"
                    id="confirmPassword"
                    name="user[password_confirmation]"
                    className="w-full px-3 py-[10px] text-[12px] text-[#495057] h-11 border-0 rounded-[20px] bg-white shadow-none appearance-none focus:outline-none font-['Poppins',sans-serif]"
                    ref={confirmPasswordRef}
                  />
                  {passwordErrors.password_confirmation && (
                    <span className="text-[#e84b3d] text-[12px] float-right">{passwordErrors.password_confirmation}</span>
                  )}
                </div>
              </div>
              {requirePasswordConfirmation && (
                <>
                  <div className="flex flex-wrap mb-2">
                    <label className="w-full md:w-[66.666667%] text-[12px] py-[5px]">
                      {t('views.devise.registrations.inform_password', 'Por questões de segurança, informe sua senha para confirmar alteração')}
                    </label>
                  </div>
                  <div className="flex flex-wrap mb-4">
                    <label htmlFor="currentPasswordChange" className="w-full md:w-1/4 text-[12px] py-[5px] md:pr-[10px]">
                      {t('views.devise.registrations.password', 'Senha atual')}
                    </label>
                    <div className="w-full md:w-[41.666667%]">
                      <input
                        type="password"
                        id="currentPasswordChange"
                        name="user[current_password]"
                        className="w-full px-3 py-[10px] text-[12px] text-[#495057] h-11 border-0 rounded-[20px] bg-white shadow-none appearance-none focus:outline-none font-['Poppins',sans-serif]"
                        ref={changePasswordRef}
                      />
                      {passwordErrors.current_password && (
                        <span className="text-[#e84b3d] text-[12px] float-right">{passwordErrors.current_password}</span>
                      )}
                    </div>
                  </div>
                </>
              )}
              <div className="flex flex-wrap mb-4">
                <div className="hidden md:block md:w-1/4"></div>
                <div className="w-full md:w-[41.666667%] flex justify-end">
                  <button 
                    type="submit" 
                    className="w-full md:w-auto px-6 py-2 bg-[#2988b5] !text-white !opacity-100 text-[12px] font-semibold rounded-[20px] min-w-[157px] text-center border-0 hover:bg-[#4a9abf] disabled:!opacity-50 disabled:cursor-not-allowed"
                    disabled={isSubmittingPassword}
                  >
                    {t('views.devise.registrations.submit_password', 'Alterar senha')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Manage Dependents */}
      <div className="w-full md:w-1/2 px-[15px]">
        <div className="p-4">
          <div className="mt-5">
            <p className="text-[#6c757d] text-[14px] font-medium mb-4">
              <span>{t('views.devise.registrations.manage_dependent', 'Gerencie seus dependentes')}</span>
            </p>
            <div>
              {!showAddDependent && (
                <button 
                  className="w-full md:w-auto justify-center px-6 py-2 bg-[#2988b5] !text-white !opacity-100 text-[12px] font-semibold rounded-[20px] min-w-[157px] text-center border-0 hover:bg-[#4a9abf] flex items-center gap-2"
                  onClick={() => setShowAddDependent(true)}
                >
                  <i className="fa fa-user"></i>
                  {t('views.devise.registrations.add_dependent', 'Adicionar Membro')}
                </button>
              )}
              {showAddDependent && (
                <form onSubmit={handleAddDependent}>
                  <div className="flex flex-wrap mb-4">
                    <label htmlFor="dependentEmail" className="w-full md:w-1/4 text-[12px] py-[5px] md:pr-[10px]">
                      {t('views.devise.registrations.email', 'Email')}
                    </label>
                    <div className="w-full md:w-[41.666667%]">
                      <input
                        type="text"
                        id="dependentEmail"
                        className="w-full px-3 py-[10px] text-[12px] text-[#495057] h-11 border-0 rounded-[20px] bg-white shadow-none appearance-none focus:outline-none font-['Poppins',sans-serif]"
                        value={newDependentEmail}
                        onChange={(e) => setNewDependentEmail(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap mb-4">
                    <div className="hidden md:block md:w-1/4"></div>
                    <div className="w-full md:w-[41.666667%] flex justify-end">
                      <button 
                        type="submit" 
                        className="w-full md:w-auto justify-center px-6 py-2 bg-[#2988b5] !text-white !opacity-100 text-[12px] font-semibold rounded-[20px] min-w-[157px] text-center border-0 hover:bg-[#4a9abf] disabled:!opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        disabled={isSubmittingDependent}
                      >
                        <i className="fa fa-user"></i>
                        {t('views.devise.registrations.save_dependent', 'Salvar Membro')}
                      </button>
                    </div>
                  </div>
                </form>
              )}
              <ul className="mt-3 list-none p-0">
                {dependents.map((dependent) => (
                  <li key={dependent.id} className="py-2 border-b border-[#eee] flex items-center gap-2">
                    {dependent.confirmed === false ? (
                      <i
                        className="fa fa-clock-o text-[#f0ad4e]"
                        title={t('views.devise.registrations.pending_confirmation', 'Aguardando confirmação')}
                      ></i>
                    ) : (
                      <i className="fa fa-user text-[#6c757d]"></i>
                    )}
                    <span className="text-[14px]">
                      {t('views.devise.registrations.name', 'Nome')}: {dependent.fullName}  |  {t('views.devise.registrations.email', 'Email')}: {dependent.email}
                    </span>
                    <a 
                      href="#" 
                      className="text-[#e84b3d] hover:text-[#c0392b] ml-2 flex items-center gap-1"
                      onClick={(e) => { 
                        e.preventDefault(); 
                        handleDeleteDependent(dependent.id); 
                      }}
                    >
                      <i className="fa fa-trash"></i>
                      {t('views.devise.registrations.exclude', 'excluir')}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
